import React, { createContext, useCallback, useEffect, useState } from "react";

import { Role } from "../types";
import { notImplemented } from "../services/Library";
import { useAuth0 } from "@auth0/auth0-react";
import useDatabroker from "../hooks/useDatabroker";

interface ServicingUser {
    SysUserID?: number;
    AuthID: string;
    UserId: string;
    username?: string;
    email?: string;
}

interface AuthContextProps {
    servicingUser?: ServicingUser;
    setServicingUser: (user: ServicingUser | undefined) => void;
    userRoles?: Role[];
    userHasRole: (role: Role) => boolean;
    userHasAnyRole: (roles: Role[]) => boolean;
}
export const AuthContext = createContext<AuthContextProps>({
    setServicingUser: notImplemented,
    userHasRole: notImplemented,
    userHasAnyRole: notImplemented
});

interface AuthContextProviderProps {
    children: React.ReactNode;
}

const AuthContextProvider: React.FC<AuthContextProviderProps> = ({ children }: AuthContextProviderProps) => {
    const { isAuthenticated, user } = useAuth0();
    const [servicingUser, setServicingUser] = useState<ServicingUser | undefined>();
    const [userRoles, setUserRoles] = useState<Role[] | undefined>();
    const databroker = useDatabroker();

    const getUserID = useCallback(
        async (AuthID: string): Promise<string | undefined> => {
            const { error, data } = await databroker.search<ServicingUser>({
                objectName: "MedDiag.dbo.aspnet_Users",
                select: ["UserId"],
                where: { AuthID }
            });
            if (error) {
                throw new Error(`Error getting user id: ${error}`);
            }
            if (data && data.length) {
                return data[0].UserId;
            }
            window.alert(`No user id found for logged in user. The application will not function as expected`);
            return undefined;
        },
        [databroker]
    );

    const getSysUserID = useCallback(
        async (userID: string): Promise<number | undefined> => {
            const { error, data } = await databroker.search<ServicingUser>({
                objectName: "MedDiag.dbo.tSysUser",
                select: ["SysUserID"],
                where: { UserId: userID }
            });
            if (error) {
                throw new Error(`Error getting user id: ${error}`);
            }
            if (data && data.length) {
                return data[0].SysUserID;
            }
            window.alert(`No user id found for logged in user. The application will not function as expected`);
            return undefined;
        },
        [databroker]
    );

    const getUserRoles = useCallback(
        async (userID: string): Promise<Role[]> => {
            const { error, data } = await databroker.query<{ RoleName: Role }>({
                from: { objectName: "MedDiag.dbo.aspnet_UsersInRoles", as: "ur" },
                join: [
                    {
                        from: { objectName: "MedDiag.dbo.aspnet_Roles", as: "r" },
                        type: "inner",
                        on: ["ur.RoleId", "r.RoleId"]
                    }
                ],
                select: ["r.RoleName"],
                where: { "ur.UserId": userID }
            });
            if (error || !data) {
                throw new Error(`Error getting user roles: ${error}`);
            }
            return data.map((d) => d.RoleName);
        },
        [databroker]
    );

    useEffect(() => {
        if (!isAuthenticated || !user) return;
        const { email, nickname, sub: AuthID } = user;
        (async () => {
            const userID = await getUserID(AuthID as string);
            const roles = await getUserRoles(userID as string);
            const sysUserID = await getSysUserID(userID as string); //not sure if this is needed
            setServicingUser({
                SysUserID: sysUserID,
                AuthID: AuthID as string,
                UserId: userID as string,
                username: nickname,
                email
            });
            setUserRoles(roles);
        })();
    }, [getSysUserID, getUserID, getUserRoles, isAuthenticated, user]);

    const userHasRole = useCallback(
        (role: Role) => {
            return !!userRoles?.includes(role);
        },
        [userRoles]
    );

    const userHasAnyRole = useCallback(
        (roleList: Role[]) => {
            return roleList.some((rl) => userRoles?.includes(rl));
        },
        [userRoles]
    );

    return (
        <AuthContext.Provider
            value={{
                servicingUser,
                setServicingUser,
                userRoles,
                userHasRole,
                userHasAnyRole
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContextProvider;
